/**
 * GameControls Component
 * Controls for the Snakes and Ladders game: position display, manual input, dice, reset
 */

import { useState, useEffect } from 'react';
import { Dice } from './Dice';

export const GameControls = ({
    position,
    maxPosition,
    onPositionChange,
    onRoll,
    lastRollResult,  // { dice: [3, 5], total: 8 }
    isWon,
    onReset,
    moveNarrative,
    diceConfig = { count: 1, sides: 6 },
    animationSpeed = 'normal'  // 'fast', 'normal', 'slow'
}) => {
    const [manualPosition, setManualPosition] = useState(position.toString());

    // Sync manual input when position changes externally
    useEffect(() => {
        setManualPosition(position.toString());
    }, [position]);

    const handleManualChange = (e) => {
        setManualPosition(e.target.value);
    };

    const handleManualSubmit = (e) => {
        e.preventDefault();
        const newPos = parseInt(manualPosition, 10);
        if (newPos >= 1 && newPos <= maxPosition) {
            onPositionChange(newPos);
        } else {
            setManualPosition(position.toString());
        }
    };

    const handleDiceRoll = (result) => {
        if (onRoll) {
            onRoll(result);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Position Display */}
            <div className="flex items-center justify-between">
                <div className="text-lg font-medium text-gray-900">
                    Position: <span className="text-badger-purple">{position}</span>
                    {position === 0 && <span className="text-gray-500 text-sm ml-2">(not started)</span>}
                </div>
                {isWon && (
                    <div className="text-lg font-bold text-green-600 animate-pulse">
                        YOU WIN!
                    </div>
                )}
            </div>

            {/* Dice */}
            <div className="py-2">
                <Dice
                    onRoll={handleDiceRoll}
                    disabled={isWon}
                    lastResult={lastRollResult}
                    diceCount={diceConfig.count}
                    diceSides={diceConfig.sides}
                    animationSpeed={animationSpeed}
                />
            </div>

            {/* Manual Position Input */}
            <div className="border-t border-gray-200 pt-4 mt-2">
                <form onSubmit={handleManualSubmit} className="flex items-center gap-2">
                    <label htmlFor="manual-position" className="text-sm text-gray-600">
                        Set position:
                    </label>
                    <input
                        id="manual-position"
                        type="number"
                        min={1}
                        max={maxPosition}
                        value={manualPosition}
                        onChange={handleManualChange}
                        className="w-20 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-badger-purple"
                    />
                    <button
                        type="submit"
                        className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded transition-colors"
                    >
                        Go
                    </button>
                </form>
            </div>

            {/* Move Narrative */}
            {moveNarrative && (
                <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-200">
                    {moveNarrative}
                </div>
            )}

            {/* Reset Button */}
            {(position > 1 || isWon) && (
                <button
                    onClick={onReset}
                    className="text-sm text-gray-600 hover:text-gray-900 underline"
                >
                    Reset Game
                </button>
            )}
        </div>
    );
};

export default GameControls;
